import { localizeServerMessage, t } from '../../i18n';
import { createDefaultContentJson, createDefaultLayoutJson, normalizeContentJson, normalizeLayoutJson } from './adLayoutDefaults';
import { getAdminAuthHeaders, handleAdminUnauthorized } from '../../auth/adminSession';
// src/components/ads/adsStroage.js
const API_BASE = (
    process.env.REACT_APP_ADMIN_API_BASE_URL ||
    process.env.REACT_APP_ADMIN_API_BASE ||
    process.env.REACT_APP_API_BASE_URL ||
    process.env.REACT_APP_API_BASE ||
    (process.env.NODE_ENV === 'development' ? 'http://localhost:3100' : '')
).trim().replace(/\/$/, '');

function extractHostedAssetPath(rawValue) {
    const normalized = String(rawValue || '').trim().replace(/\\/g, '/');
    if (!normalized)
        return '';
    const lower = normalized.toLowerCase();
    for (const marker of ['/ad-uploads/', '/uploads/']) {
        const idx = lower.lastIndexOf(marker);
        if (idx >= 0)
            return normalized.slice(idx);
    }
    if (lower.startsWith('ad-uploads/'))
        return `/${normalized}`;
    if (lower.startsWith('uploads/'))
        return `/${normalized}`;
    return '';
}

export function resolveAdminAssetUrl(rawValue) {
    const normalized = String(rawValue || '').trim().replace(/\\/g, '/');
    if (!normalized)
        return '';
    if (/^(data:|blob:)/i.test(normalized))
        return normalized;

    // 과거 DB에 서버 내부 상대경로가 URL에 섞여 저장된 경우도 공개 mount 기준으로 복구한다.
    const hostedPath = extractHostedAssetPath(normalized);
    if (hostedPath)
        return API_BASE ? `${API_BASE}${hostedPath}` : hostedPath;

    if (/^https?:\/\//i.test(normalized))
        return normalized;
    if (normalized.startsWith('/'))
        return API_BASE ? `${API_BASE}${normalized}` : normalized;
    return API_BASE ? `${API_BASE}/${normalized}` : normalized;
}

function normalizeLayoutAssetUrls(layoutJson) {
    if (!layoutJson || typeof layoutJson !== 'object' || Array.isArray(layoutJson))
        return layoutJson || {};
    const layout = { ...layoutJson };
    if (Array.isArray(layout.assets)) {
        layout.assets = layout.assets.map((asset) => {
            if (!asset || typeof asset !== 'object')
                return asset;
            const rawPath = String(asset.path || '').trim();
            const rawUrl = String(asset.url || '').trim();
            return {
                ...asset,
                path: extractHostedAssetPath(rawPath) || extractHostedAssetPath(rawUrl) || rawPath.replace(/\\/g, '/'),
                url: resolveAdminAssetUrl(rawUrl || rawPath),
            };
        });
    }
    return layout;
}

function normalizeAdRecord(ad) {
    if (!ad || typeof ad !== 'object')
        return ad;
    const rawImagePath = ad.imagePath || ad.image_path || '';
    const rawImageUrl = ad.imageUrl || ad.image_url || rawImagePath;
    return {
        ...ad,
        imagePath: extractHostedAssetPath(rawImagePath) || extractHostedAssetPath(rawImageUrl) || String(rawImagePath || '').replace(/\\/g, '/'),
        imageUrl: resolveAdminAssetUrl(rawImageUrl),
        layoutJson: normalizeLayoutAssetUrls(ad.layoutJson || ad.layout_json || {}),
    };
}
export const AREAS = [
    { value: 'main', label: t("\uBA54\uC778 \uC601\uC5ED") },
    { value: 'payment', label: t("\uACB0\uC81C \uC601\uC5ED") },
];
export const DEVICES = [
    { value: 'desktop', label: 'Desktop' },
    { value: 'mobile', label: 'Mobile' },
];
export function makeEmptyAd({ area = 'main', writer = 'admin' } = {}) {
    return {
        id: null,
        area,
        device: 'desktop',
        country: 'ALL',
        label: '',
        clickUrl: '',
        order: 100,
        adminStatus: 'ACTIVE',
        startAt: '',
        endAt: '',
        imagePath: '',
        imageUrl: '',
        originalFileName: '',
        mimeType: '',
        fileSize: 0,
        writer,
        templateKey: 'FREEFORM_V1',
        backgroundMode: 'COVER',
        contentJson: createDefaultContentJson(),
        layoutJson: createDefaultLayoutJson(),
        createdAt: null,
        updatedAt: null,
    };
}
export function parseDT(v) {
    if (!v)
        return null;
    const normalized = String(v).replace(' ', 'T');
    const d = new Date(normalized);
    return Number.isNaN(d.getTime()) ? null : d;
}
export function computeRuntimeStatus(ad, now = new Date()) {
    if (!ad)
        return 'DISABLED';
    if (ad.adminStatus === 'DISABLED')
        return 'DISABLED';
    if (ad.adminStatus === 'PENDING')
        return 'PENDING';
    const s = parseDT(ad.startAt);
    const e = parseDT(ad.endAt);
    if (e && e.getTime() < now.getTime())
        return 'EXPIRED';
    if (s && s.getTime() > now.getTime())
        return 'SCHEDULED';
    return 'ACTIVE';
}
export function sortAds(list) {
    const now = new Date();
    return [...(list || [])]
        .map((x) => ({ ...x, _runtimeStatus: computeRuntimeStatus(x, now) }))
        .sort((a, b) => {
        const ao = Number.isFinite(Number(a.order)) ? Number(a.order) : 100;
        const bo = Number.isFinite(Number(b.order)) ? Number(b.order) : 100;
        if (ao !== bo)
            return ao - bo;
        const at = new Date(a.updatedAt || a.createdAt || 0).getTime();
        const bt = new Date(b.updatedAt || b.createdAt || 0).getTime();
        return bt - at;
    });
}
function getAuthHeaders() {
    return getAdminAuthHeaders();
}

async function fetchJson(url, options = {}) {
    const res = await fetch(url, {
        credentials: 'include',
        ...options,
        headers: {
            ...(options.headers || {}),
            ...getAuthHeaders(),
        },
    });
    handleAdminUnauthorized(res.status, url);

    const text = await res.text();
    let body = null;
    try {
        body = text ? JSON.parse(text) : null;
    }
    catch {
        body = null;
    }
    if (!res.ok) {
        throw new Error(localizeServerMessage(body?.message, '요청 실패 ({status})', { status: res.status }));
    }
    return body?.data ?? body ?? null;
}
function buildQuery(params = {}) {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
        if (value == null || value === '' || value === 'all')
            return;
        qs.set(key, String(value));
    });
    const q = qs.toString();
    return q ? `?${q}` : '';
}
function buildFormData(payload, file, options = {}) {
    const fd = new FormData();
    const data = {
        area: payload.area,
        device: payload.device,
        country: String(payload.country || 'ALL').toUpperCase(),
        label: payload.label,
        clickUrl: payload.clickUrl || '',
        order: payload.order ?? 100,
        adminStatus: payload.adminStatus || 'ACTIVE',
        startAt: payload.startAt || '',
        endAt: payload.endAt || '',
        writer: payload.writer || 'admin',
        keepImage: options.keepImage === false ? 'false' : 'true',
        templateKey: payload.templateKey || 'FREEFORM_V1',
        backgroundMode: payload.backgroundMode || 'COVER',
        contentJson: JSON.stringify(normalizeContentJson(payload.contentJson)),
        layoutJson: JSON.stringify(normalizeLayoutJson(payload.layoutJson)),
    };
    Object.entries(data).forEach(([key, value]) => {
        fd.append(key, value == null ? '' : String(value));
    });
    if (file) {
        fd.append('image', file);
    }
    const elementFiles = Array.isArray(options.elementFiles) ? options.elementFiles : [];
    const manifest = [];
    elementFiles.forEach((item) => {
        if (!item?.id || !item?.file) return;
        manifest.push({ id: String(item.id), name: item.file.name || '' });
        fd.append('elementImages', item.file);
    });
    fd.append('elementImageManifest', JSON.stringify(manifest));
    fd.append('removedAssetIds', JSON.stringify(Array.isArray(options.removedAssetIds) ? options.removedAssetIds : []));
    return fd;
}
export async function listAds(params = {}) {
    const data = await fetchJson(`${API_BASE}/admin/ads${buildQuery(params)}`);
    return sortAds(Array.isArray(data) ? data.map(normalizeAdRecord) : []);
}
export async function createAd(payload, file, options = {}) {
    const data = await fetchJson(`${API_BASE}/admin/ads`, {
        method: 'POST',
        body: buildFormData(payload, file, { ...options, keepImage: false }),
    });
    return normalizeAdRecord(data);
}
export async function updateAd(id, payload, file, options = {}) {
    const data = await fetchJson(`${API_BASE}/admin/ads/${id}`, {
        method: 'PUT',
        body: buildFormData(payload, file, options),
    });
    return normalizeAdRecord(data);
}
export async function deleteAd(id) {
    return fetchJson(`${API_BASE}/admin/ads/${id}`, {
        method: 'DELETE',
    });
}
export async function reorderAds(items = []) {
    return fetchJson(`${API_BASE}/admin/ads/reorder`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items }),
    });
}
export async function fetchPublicAds(params = {}) {
    const data = await fetchJson(`${API_BASE}/public/ads${buildQuery(params)}`);
    return sortAds(Array.isArray(data) ? data.map(normalizeAdRecord) : []);
}
